import { useEffect, useRef } from 'react'
import { logger } from '@/utils/logger'
import { getFlagDisplay } from '@/data/countryFlags'
import { COUNTRY_CODES } from '@/data/countryCodes'

// jQuery-like interface for form elements
interface JQueryElement {
  find: (selector: string) => JQueryElement
  append: (element: HTMLElement | string) => void
  val: (value?: string) => string | JQueryElement
  change: () => JQueryElement
  on: (event: string, handler: () => void) => void
  trigger: (event: string) => JQueryElement
  length: number
}

/**
 * Hook to enhance existing HubSpot forms with phone extension functionality
 * This is a replacement for the class-based PhoneExtensionDropdown
 * that works with the existing jQuery-based form structure
 */
export const usePhoneExtensionEnhancer = (form: JQueryElement | null) => {
  const cleanupRef = useRef<(() => void)[]>([])
  const isInitializedRef = useRef(false)

  useEffect(() => {
    if (!form || !form.length || isInitializedRef.current) return

    const phoneWrapper = form.find('.hs-fieldtype-intl-phone')
    const countrySelect = form.find('select[id^="phone_ext-"]')
    const visibleInput = form.find('input[type="tel"]')
    const hiddenInput = form.find('input[name="phone"]')

    if (!phoneWrapper.length) return

    // Mark as initialized
    isInitializedRef.current = true

    // Detect Windows
    const isWindows = typeof navigator !== 'undefined' && navigator.platform?.toLowerCase().includes('win')

    // Add styles
    const addStyles = () => {
      const styles = `
        .hs-fieldtype-intl-phone {
          position: relative;
        }

        .hs-fieldtype-intl-phone select {
          padding-left: 30px !important;
          background-position: calc(100% - 5px) center !important;
          color: transparent;
          text-shadow: 0 0 0 transparent;
        }

        .hs-fieldtype-intl-phone select option {
          color: initial;
        }

        .country-flag {
          position: absolute;
          left: 8px;
          transform: translateY(-50%);
          z-index: 1;
          pointer-events: none;
          top: ${isWindows ? '45%' : '50%'};
          font-size: 16px;
        }

        .country-name {
          margin-left: 25px;
        }
        
        ${
          isWindows
            ? `
          .hs-fieldtype-intl-phone select {
            padding-left: 35px !important;
          }
        `
            : ''
        }
      `

      if (!document.getElementById('phone-extension-styles')) {
        const styleSheet = document.createElement('style')
        styleSheet.id = 'phone-extension-styles'
        styleSheet.textContent = styles
        document.head.appendChild(styleSheet)

        // Add cleanup for styles
        cleanupRef.current.push(() => {
          const existingStyle = document.getElementById('phone-extension-styles')
          if (existingStyle) {
            existingStyle.remove()
          }
        })
      }
    }

    // Update phone display
    const updatePhoneDisplay = () => {
      const countryCode = countrySelect.val() as string
      const phoneCode = COUNTRY_CODES[countryCode] || ''

      // Update flag display
      if (flagDisplay) {
        flagDisplay.textContent = getFlagDisplay(countryCode, isWindows)
      }

      // Update visible input if it doesn't start with the country code
      let currentNumber = (visibleInput.val() as string) || ''
      if (!currentNumber.startsWith(phoneCode)) {
        currentNumber = currentNumber.replace(/^\+\d+\s*/, '')
        visibleInput.val(`${phoneCode} ${currentNumber}`.trim())
      }

      // Update hidden input
      const fullNumber = visibleInput.val() as string
      hiddenInput.val(fullNumber)
      hiddenInput.trigger('change')
    }

    // Enhance select options
    const enhanceSelectOptions = () => {
      // Add flags to all options
      const options = countrySelect.find('option') as any

      options.each((index: number, option: HTMLOptionElement) => {
        const countryCode = option.value
        const flag = getFlagDisplay(countryCode, isWindows)
        const originalText = option.text

        // Don't modify if already enhanced
        if (!originalText.includes('🏳️')) {
          option.text = `${flag} ${originalText}`
        }
      })

      // Add flag display next to select
      flagDisplayElement = document.createElement('span')
      flagDisplayElement.className = 'country-flag'
      flagDisplayElement.textContent = getFlagDisplay(countrySelect.val() as string, isWindows)
      phoneWrapper.append(flagDisplayElement)

      return flagDisplayElement
    }

    // Initialize
    let flagDisplayElement: HTMLElement
    let flagDisplay: HTMLElement | null = null

    try {
      addStyles()
      flagDisplay = enhanceSelectOptions()

      // Attach event listeners
      const handleCountryChange = () => updatePhoneDisplay()
      const handlePhoneInput = () => {
        const fullNumber = visibleInput.val() as string
        hiddenInput.val(fullNumber)
        hiddenInput.trigger('change')
      }

      countrySelect.on('change', handleCountryChange)
      visibleInput.on('input', handlePhoneInput)

      // Set initial values
      updatePhoneDisplay()

      // Store cleanup functions
      cleanupRef.current.push(() => {
        // Remove flag display element
        if (flagDisplayElement && flagDisplayElement.parentNode) {
          flagDisplayElement.parentNode.removeChild(flagDisplayElement)
        }
      })
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))

      logger.error('Phone extension enhancer initialization failed:', { message: err.message, name: err.name, stack: err.stack })
    }

    // Cleanup function
    return () => {
      cleanupRef.current.forEach((cleanup) => cleanup())
      cleanupRef.current = []
      isInitializedRef.current = false
    }
  }, [form])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupRef.current.forEach((cleanup) => cleanup())
      cleanupRef.current = []
      isInitializedRef.current = false
    }
  }, [])
}

// Backward compatibility: Create a function that mimics the old class constructor
export const createPhoneExtensionEnhancer = (form: JQueryElement) => {
  // Use the hook logic but in a non-hook context
  const phoneWrapper = form.find('.hs-fieldtype-intl-phone')
  if (!phoneWrapper.length) return

  // Simplified version for backward compatibility
  const isWindows = typeof navigator !== 'undefined' && navigator.platform?.toLowerCase().includes('win')

  // Add styles
  const styles = `
    .hs-fieldtype-intl-phone {
      position: relative;
    }
    .hs-fieldtype-intl-phone select {
      padding-left: 30px !important;
      color: transparent;
      text-shadow: 0 0 0 transparent;
    }
    .hs-fieldtype-intl-phone select option {
      color: initial;
    }
    .country-flag {
      position: absolute;
      left: 8px;
      top: ${isWindows ? '45%' : '50%'};
      transform: translateY(-50%);
      z-index: 1;
      pointer-events: none;
      font-size: 16px;
    }
  `

  if (!document.getElementById('phone-extension-styles')) {
    const styleSheet = document.createElement('style')
    styleSheet.id = 'phone-extension-styles'
    styleSheet.textContent = styles
    document.head.appendChild(styleSheet)
  }

  // Add flag display
  const countrySelect = form.find('select[id^="phone_ext-"]')
  const flagDisplay = document.createElement('span')
  flagDisplay.className = 'country-flag'
  flagDisplay.textContent = getFlagDisplay(countrySelect.val() as string, isWindows)
  phoneWrapper.append(flagDisplay)

  // Update flag on change
  countrySelect.on('change', () => {
    flagDisplay.textContent = getFlagDisplay(countrySelect.val() as string, isWindows)
  })
}

export default usePhoneExtensionEnhancer
